import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Formik } from 'formik';
import { components, OptionProps } from 'react-select';
import ContentContainer from 'templates/content-container/contentContainer';
import ContentContainerToolbar from 'ui-components/molecules/content-container-toolbar/contentContainerToolbar';
import WeekNavigator from './components/weekNavigator';
import DispatchBoard, { DispatchBoardFilters } from './components/dispatchBoard';
import SchedulerActionsMenu from './components/schedulerActionsMenu';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import UpdateWeek from './sidebars/updateWeek';
import { useWeeks } from './hooks/useWeek';
import { useLanesByDay } from './hooks/useLanes';
import { setSelectedScheduleType, setSelectedWeekStartDate, staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import { getRegionsServiceCenters, serviceCentersSelector } from 'slices/service-centers/serviceCentersSlice';
import './linehaulScheduler.scss';
import './dashboard.scss';
import '../terminal-selector/terminalSelector.scss';
import UpdateSidebar from './sidebars/updateSidebar';
import TargetWeek from './sidebars/targetWeek';
import { ReactComponent as PaperDocumentIcon } from "images/paper_document.svg";
import ActivityLogSidebar from './sidebars/activityLog';
import ReviewDispatchSidebar from './sidebars/reviewDispatch';
import WeekSummaryTiles from './components/weekSummaryTiles';
import { useSummaryTiles } from './hooks/useSummaryTiles';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import { XGSSelectOption } from 'ui-components/xgs-select/xgsSelect';
import { LaneTerminal, Manifest, weekOptions } from 'app/data/static-load/model';
import StaticLoadPlanTable from './dashboard';
import { userSelector } from 'slices/user/userSlice';
import { UserUtils } from 'app/data/user/userUtils';

const LINEHAUL_ADMINS_GROUP = "Linehaul Admins";

type SchedulerViewMode = 'week' | 'day';

// ── helpers ──────────────────────────────────────────────────────────────────

/** Return the Monday of the week containing `date` */
function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay(); // 0 = Sun
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
}

/** Add `n` days to a date (returns a new Date) */
function addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

const formatTerminalOptionName = (terminal: {
    airportCode?: string;
    terminalName?: string | null;
}) => {
    const name = terminal.terminalName
        ? terminal.terminalName.toLowerCase().capitalize()
        : "";
    if (terminal.airportCode && name) {
        return `${terminal.airportCode} - ${name}`;
    }
    return terminal.airportCode || name || "";
};

const buildTerminalOptions = (
    terminals: Array<LaneTerminal | null | undefined>
): XGSSelectOption[] => {
    const byCode = new Map<string, XGSSelectOption>();

    terminals.forEach((terminal) => {
        if (!terminal) return;
        const code = terminal.airportCode?.trim();
        if (!code || byCode.has(code)) return;

        byCode.set(code, {
            value: code,
            label: formatTerminalOptionName(terminal),
            subtitle:
                terminal.terminalNumber !== undefined && terminal.terminalNumber !== null
                    ? String(terminal.terminalNumber)
                    : undefined,
        });
    });

    return Array.from(byCode.values()).sort((a, b) =>
        String(a.label).localeCompare(String(b.label))
    );
};

const buildOriginOptions = (lanes: Manifest[]): XGSSelectOption[] =>
    buildTerminalOptions(lanes.map((lane) => lane.origin));

const buildStopOptions = (lanes: Manifest[]): XGSSelectOption[] =>
    buildTerminalOptions(lanes.flatMap((lane) => lane.stops ?? []));

const buildDestinationOptions = (lanes: Manifest[]): XGSSelectOption[] =>
    buildTerminalOptions(lanes.map((lane) => lane.destination));

const TerminalSelectOption = (props: OptionProps<XGSSelectOption>) => (
    <components.Option
        {...props}
        className={`${props.className || ""} xgs-terminal-selector__list__item`.trim()}
    >
        <div className="xgs-terminal-selector__list__item__block">
            <span className="xgs-terminal-selector__list__item__name">
                {props.data.label}
            </span>
            {props.data.subtitle && (
                <span className="xgs-terminal-selector__list__item__id">
                    {props.data.subtitle}
                </span>
            )}
        </div>
    </components.Option>
);

const terminalSelectComponents = {
    Option: TerminalSelectOption,
};

const selectMenuStyles = {
    menuPortal: (base: React.CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const LinehaulScheduler: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const staticLoadState = useSelector(staticLoadSelector);
    const userState = useSelector(userSelector);
    const serviceCentersState = useSelector(serviceCentersSelector);
    const dispatch = useDispatch();
    const { refetch: refetchWeeks } = useWeeks();
    const isLinehaulManagementEnabled = UserUtils.hasAssignedGroup(
        userState.profile,
        LINEHAUL_ADMINS_GROUP
    );

    const [isUpdateWeekOpen, setIsUpdateWeekOpen] = useState(false);
    const [isCreateLaneOpen, setIsCreateLaneOpen] = useState(false);
    const [isEditWeek, setIsEditWeek] = useState(false);
    const [modalOpen, setModalOpen] = React.useState<boolean>(false);
    const [showActivityLog, setShowActivityLog] = React.useState<boolean>(false);
    const [showReviewDispatch, setShowReviewDispatch] = React.useState<boolean>(false);
    const [selectedOrigins, setSelectedOrigins] = useState<string[]>([]);
    const [selectedStops, setSelectedStops] = useState<string[]>([]);
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const [selectedDay, setSelectedDay] = useState<string>("");
    const [selectedRegion, setSelectedRegion] = useState<string>("");
    const [viewMode, setViewMode] = useState<SchedulerViewMode>('day');

    const { data: weeks } = useWeeks();

    // 1. Derive navigable weeks from API
    const workingWeeks = React.useMemo(() => {
        if (!weeks) return [];
        return weeks
            // .filter((w) => w.scheduleType === 'WORKING')
            .map((w) => w.weekStartDate)
            .sort(); // 'YYYY-MM-DD' sorts chronologically
    }, [weeks]);

    // 2. Track which week is selected (by date string, not index — safer if list refetches)
    const [weekStartDate, setWeekStartDate] = useState<string>('');
    const [scheduleType, setScheduleType] = useState<string>('');

    const regionOptions = useMemo(
        () =>
            serviceCentersState.regions?.map(
                (region): XGSSelectOption => ({
                    label: region.regionName,
                    value: region.regionName.toLowerCase(),
                })
            ) || [],
        [serviceCentersState.regions]
    );

    const { data: lanesByDay, refetch: refetchLanesByDay } = useLanesByDay(
        staticLoadState.selectedDivision,
        weekStartDate,
        selectedRegion
    );

    const { data: summaryTiles, isLoading: isLoadingSummaryTiles, refetch: refetchSummaryTiles } = useSummaryTiles(
        staticLoadState.selectedDivision,
        weekStartDate,
        !!weekStartDate
    );

    const allLanes = useMemo(
        () => Object.values(lanesByDay ?? {}).flat(),
        [lanesByDay]
    );

    const originOptions = useMemo(() => buildOriginOptions(allLanes), [allLanes]);
    const stopOptions = useMemo(() => buildStopOptions(allLanes), [allLanes]);
    const destinationOptions = useMemo(() => buildDestinationOptions(allLanes), [allLanes]);

    const boardFilters = useMemo<DispatchBoardFilters>(
        () => ({
            origins: selectedOrigins,
            stops: selectedStops,
            destinations: selectedDestinations,
            day: selectedDay,
            region: selectedRegion,
        }),
        [selectedOrigins, selectedStops, selectedDestinations, selectedDay, selectedRegion]
    );

    useEffect(() => {
        dispatch(getRegionsServiceCenters());
    }, [dispatch]);

    // 3. Initialize once when weeks load
    useEffect(() => {
        if (workingWeeks.length > 0 && !weekStartDate) {
            const urlWeekStartDate = new URLSearchParams(location.search).get('weekStartDate');
            const initialWeek =
                urlWeekStartDate && workingWeeks.includes(urlWeekStartDate)
                    ? urlWeekStartDate
                    : workingWeeks[workingWeeks.length - 1];
            setWeekStartDate(initialWeek);
            setScheduleType(weeks?.find((w) => w.weekStartDate === initialWeek)?.scheduleType || '');
        }
    }, [workingWeeks, weekStartDate, location.search, weeks]);

    useEffect(() => {
        dispatch(setSelectedWeekStartDate(weekStartDate));
    }, [weekStartDate, dispatch]);

    useEffect(() => {
        dispatch(setSelectedScheduleType(scheduleType.toLowerCase().capitalize()));
    }, [scheduleType, dispatch]);

    // Clear origin/destination filters when week changes (options change)
    useEffect(() => {
        setSelectedOrigins([]);
        setSelectedStops([]);
        setSelectedDestinations([]);
        setSelectedDay("");
    }, [weekStartDate]);

    useEffect(() => {
        setSelectedOrigins([]);
        setSelectedStops([]);
        setSelectedDestinations([]);
    }, [selectedRegion]);

    const currentIndex = workingWeeks.indexOf(weekStartDate);
    const weekStart = weekStartDate ? new Date(weekStartDate + 'T00:00:00') : getMonday(new Date());
    const weekEnd = addDays(weekStart, 6);

    const handlePrev = () => {
        if (currentIndex > 0) {
            setWeekStartDate(workingWeeks[currentIndex - 1]);
            setScheduleType(weeks?.find((w) => w.weekStartDate === workingWeeks[currentIndex - 1])?.scheduleType || '');
        }
    };

    const handleNext = () => {
        if (currentIndex >= 0 && currentIndex < workingWeeks.length - 1) {
            setWeekStartDate(workingWeeks[currentIndex + 1]);
            setScheduleType(weeks?.find((w) => w.weekStartDate === workingWeeks[currentIndex + 1])?.scheduleType || '');
        }
    };

    const currentWeek = weeks?.find((w) => w.weekStartDate === weekStartDate);
    const editWeek = {
        weekStartDate: moment(weekStart).format('YYYY-MM-DD'),
        division: staticLoadState.selectedDivision,
        weekDescription: currentWeek?.description ?? '',
        status: currentWeek?.status ?? '',
        dispatches: currentWeek?.dispatches,
        createdAt: currentWeek?.createdAt,
        lastModifiedAt: currentWeek?.lastModifiedAt,
    };

    return <><ContentContainer mods={{ "full-width": true }}
        titleComponent={
            <div className='linehaul-scheduler-container-toolbar'>
                <ContentContainerToolbar
                    title="Linehaul Weekly Schedule">
                </ContentContainerToolbar>
            </div>
        }
    >
        <div className='linehaul-scheduler-container-toolbar-clone-existing-week'>
            {isLinehaulManagementEnabled && (
                <>
                    <Button theme={ButtonThemes.grayInverted} size={ButtonSizes.auto} onClick={() => setModalOpen(true)}>Clone Existing Week</Button>
                    <SchedulerActionsMenu
                        onEditScheduleDetails={() => {
                            setIsEditWeek(true);
                            setIsUpdateWeekOpen(true);
                        }}
                        onCreateNewSchedule={() => {
                            setIsEditWeek(false);
                            setIsUpdateWeekOpen(true);
                        }}
                        onCreateLane={() => setIsCreateLaneOpen(true)}
                        onManageSchedules={() => history.push('/static-load/manage-schedules')}
                    />
                </>
            )}
            <div onClick={() => setShowActivityLog(state => !state)} className='activity-log-icon'><PaperDocumentIcon />&nbsp; Activity Log</div>
        </div>
        <div className='linehaul-scheduler-container-week-navigator'>
            <div className='linehaul-scheduler-container-week-navigator-title'>
                <span>You are viewing the schedule for {moment(weekStart).format('MMMM D, YYYY')} - {moment(weekEnd).format('MMMM D, YYYY')}.
                    Changes apply only to this week.
                </span>
            </div>
            <div className='linehaul-scheduler-container-week-navigator-selector'>
                <div className='linehaul-scheduler-container-week-navigator-component'>
                    <WeekNavigator
                        startDate={weekStart}
                        endDate={weekEnd}
                        subtitle={`${staticLoadState.selectedScheduleType} week · Changes apply to this week only`}
                        onPrev={handlePrev}
                        onNext={handleNext}
                        prevDisabled={currentIndex <= 0}
                        nextDisabled={currentIndex < 0 || currentIndex >= workingWeeks.length - 1}
                    />
                </div>
                <div className='linehaul-scheduler-container-week-navigator-actions'>
                    <div
                        className="linehaul-scheduler-view-toggle"
                        role="tablist"
                        aria-label="Schedule view"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={viewMode === 'week'}
                            className={`linehaul-scheduler-view-toggle__item linehaul-scheduler-view-toggle__item--first${viewMode === 'week' ? ' linehaul-scheduler-view-toggle__item--active' : ''}`}
                            onClick={() => setViewMode('week')}
                        >
                            Week
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={viewMode === 'day'}
                            className={`linehaul-scheduler-view-toggle__item linehaul-scheduler-view-toggle__item--last${viewMode === 'day' ? ' linehaul-scheduler-view-toggle__item--active' : ''}`}
                            onClick={() => setViewMode('day')}
                        >
                            Day
                        </button>
                    </div>
                    {isLinehaulManagementEnabled && currentWeek?.status === "PENDING" && (
                        <Button
                            theme={ButtonThemes.blue}
                            size={ButtonSizes.small}
                            onClick={() => setShowReviewDispatch(true)}
                        >
                            Dispatch Week
                        </Button>
                    )}                
                </div>
            </div>

        </div>

        {viewMode === 'day' ? (
            <>
                <Formik
                    enableReinitialize={false}
                    initialValues={{
                        region: selectedRegion,
                        origins: selectedOrigins,
                        stops: selectedStops,
                        destinations: selectedDestinations,
                        day: selectedDay,
                    }}
                    onSubmit={() => undefined}
                >
                    <div className="dashboardFilter linehaul-scheduler-filters">
                        <XGSFormSelect
                            name="region"
                            label="Region"
                            labelMode={LabelModes.column}
                            className="dashboardFilter__select"
                            options={regionOptions}
                            isClearable
                            controlled
                            placeholder="All regions"
                            styles={selectMenuStyles}
                            value={regionOptions.find((option) => String(option.value) === selectedRegion) ?? null}
                            onValueChange={(option) => {
                                setSelectedRegion(option ? String(option.value) : "");
                            }}
                            closeMenuOnScroll={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't close when scrolling inside the menu itself
                                return !target?.closest?.(".xgs-select__menu");
                            }}
                        />
                        <XGSFormSelect
                            name="origins"
                            label="Origin"
                            labelMode={LabelModes.column}
                            className="dashboardFilter__select"
                            options={originOptions}
                            isMulti
                            isClearable
                            controlled
                            placeholder="All origins"
                            styles={selectMenuStyles}
                            components={terminalSelectComponents}
                            value={originOptions.filter((option) =>
                                selectedOrigins.includes(String(option.value))
                            )}
                            onMultiValuesChange={(selected) => {
                                setSelectedOrigins(selected?.map((item) => String(item.value)) ?? []);
                            }}
                            closeMenuOnScroll={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't close when scrolling inside the menu itself
                                return !target?.closest?.(".xgs-select__menu");
                            }}
                        />
                        <XGSFormSelect
                            name="stops"
                            label="Stop"
                            labelMode={LabelModes.column}
                            className="dashboardFilter__select"
                            options={stopOptions}
                            isMulti
                            isClearable
                            controlled
                            placeholder="All stops"
                            styles={selectMenuStyles}
                            components={terminalSelectComponents}
                            value={stopOptions.filter((option) =>
                                selectedStops.includes(String(option.value))
                            )}
                            onMultiValuesChange={(selected) => {
                                setSelectedStops(selected?.map((item) => String(item.value)) ?? []);
                            }}
                            closeMenuOnScroll={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't close when scrolling inside the menu itself
                                return !target?.closest?.(".xgs-select__menu");
                            }}
                        />
                        <XGSFormSelect
                            name="destinations"
                            label="Destination"
                            labelMode={LabelModes.column}
                            className="dashboardFilter__select"
                            options={destinationOptions}
                            isMulti
                            isClearable
                            controlled
                            placeholder="All destinations"
                            styles={selectMenuStyles}
                            components={terminalSelectComponents}
                            value={destinationOptions.filter((option) =>
                                selectedDestinations.includes(String(option.value))
                            )}
                            onMultiValuesChange={(selected) => {
                                setSelectedDestinations(
                                    selected?.map((item) => String(item.value)) ?? []
                                );
                            }}
                            closeMenuOnScroll={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't close when scrolling inside the menu itself
                                return !target?.closest?.(".xgs-select__menu");
                            }}
                        />
                        <XGSFormSelect
                            name="day"
                            label="Day of week"
                            labelMode={LabelModes.column}
                            className="dashboardFilter__select"
                            options={weekOptions}
                            isClearable
                            controlled
                            placeholder="All days"
                            styles={selectMenuStyles}
                            value={weekOptions.find((option) => String(option.value) === selectedDay) ?? null}
                            onValueChange={(option) => {
                                setSelectedDay(option ? String(option.value) : "");
                            }}
                            closeMenuOnScroll={(e) => {
                                const target = e.target as HTMLElement;
                                // Don't close when scrolling inside the menu itself
                                return !target?.closest?.(".xgs-select__menu");
                            }}
                        />
                    </div>
                </Formik>

                <WeekSummaryTiles summary={summaryTiles} isLoading={isLoadingSummaryTiles} />

                <div className='linehaul-scheduler-container-dispatch-board'>
                    <DispatchBoard filters={boardFilters} readOnly={!isLinehaulManagementEnabled} />
                </div>
            </>
        ) : (
            <div className="linehaul-scheduler-week-view">
                <StaticLoadPlanTable
                    embedded
                    readOnly={!isLinehaulManagementEnabled}
                    region={selectedRegion}
                    onRegionChange={setSelectedRegion}
                    regionOptions={regionOptions}
                />
            </div>
        )}
    </ContentContainer>
        {isLinehaulManagementEnabled && (
            <>
                <UpdateWeek
                    isOpen={isUpdateWeekOpen}
                    onClose={() => setIsUpdateWeekOpen(false)}
                    isLoading={false}
                    refetchGetWeeks={refetchWeeks}
                    isEdit={isEditWeek}
                    editWeek={editWeek}
                />
                <UpdateSidebar
                    isOpen={isCreateLaneOpen}
                    title="Create Lane"
                    onClose={() => {
                        setIsCreateLaneOpen(false);
                    }}
                    data={{ lane: {}, drops: [] }}
                    isLoading={false}
                    terminalMap={{}}
                    isEditMode={"create"}
                    refetchLane={() => { }}
                    refetchLaneByDay={() => {
                        refetchLanesByDay();
                        refetchSummaryTiles();
                    }}
                    handleAddDrop={() => { }}
                />
                <TargetWeek
                    isOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    weekStartDate={weekStartDate}
                    division={staticLoadState.selectedDivision}
                    onCloneSuccess={refetchWeeks}
                />
                <ReviewDispatchSidebar
                    isOpen={showReviewDispatch}
                    onClose={() => setShowReviewDispatch(false)}
                    onDispatchSuccess={() => {
                        refetchWeeks();
                        refetchSummaryTiles();
                    }}
                />
            </>
        )}
        <ActivityLogSidebar
            isOpen={showActivityLog}
            onClose={() => setShowActivityLog(false)}
        />
    </>
};

export default LinehaulScheduler;
